import {
  ExecutionMethod,
} from "appwrite";

import {
  config,
  functions,
} from "./appwrite";

export type CloudRow = {
  $id: string;
  $createdAt: string;
  $updatedAt: string;
};

export type TaskRow =
  CloudRow & {
    userId: string;
    localId: number;
    title: string;
    note: string;
    dueAt: number;
    repeatRule: string;
    routine: boolean;
    completed: boolean;
    icon?: string;
    locationName?: string;
    latitude?: number;
    longitude?: number;
    notificationsEnabled?: boolean;
    deleted?: boolean;
    createdAt?: number;
    deviceId?: string;
    syncVersion?: number;
    updatedAt: number;
  };

export type CountdownRow =
  CloudRow & {
    userId: string;
    localId: number;
    title: string;
    category: string;
    eventAt: number;
    journeyEnabled: boolean;
    icon?: string;
    note?: string;
    deleted?: boolean;
    createdAt?: number;
    deviceId?: string;
    syncVersion?: number;
    updatedAt: number;
  };

export type EventRow =
  CloudRow & {
    userId: string;
    localId: number;
    title: string;
    category: string;
    iconKey: string;
    startAt: number;
    endAt: number;
    allDay: boolean;
    timeZone: string;

    location: string;
    latitude?: number;
    longitude?: number;
    placeId?: string;

    notes: string;
    imageUri: string;
    cloudImageFileId: string;
    repeatRule: string;
    addToCountdown: boolean;
    linkedCountdownId:
      | number
      | string;
    journeyEnabled: boolean;
    deleted: boolean;
    createdAt: number;
    updatedAt: number;
  };

export type MalaysiaHoliday = {
  id: string;
  name: string;
  date: string;
  states: string[];
  type:
    | "PUBLIC"
    | "STATE"
    | "REPLACEMENT";
  tentative: boolean;
  source: string;
  sourceUrl: string;
};

export type MalaysiaHolidayResponse = {
  country: "Malaysia";
  year: number;
  regions: string[];
  holidays: MalaysiaHoliday[];
  source: string;
  sourceUrl: string;
  fetchedAt: string;
  cached: boolean;
};

export type RoutineCompletionRow =
  CloudRow & {
    userId: string;
    taskRowId: string;
    taskLocalId?: number;
    dayStart: number;
    completed: boolean;
    updatedAt: number;
  };

export type UserPreferencesRow =
  CloudRow & {
    userId: string;

    theme:
      | "SYSTEM"
      | "LIGHT"
      | "DARK";

    style:
      | "LIQUID_GLASS"
      | "SPATIAL"
      | "MINIMAL";

    myState: string;
    timeZone: string;
    notificationsEnabled: boolean;
    updatedAt: number;
  };

export type ProfileIdentity = {
  displayName: string;
  username: string;
  badgeNumber: number;
  owner: boolean;
};

export type EntitlementRow =
  CloudRow & {
    userId: string;
    plan: string;
    products: string[];
    subscriptionState: string;
    expiryAt: number;
    verifiedAt: number;
  };

export type CreateTaskInput = {
  title: string;
  note?: string;
  dueAt: number;
  repeatRule?: string;
  routine?: boolean;
  icon?: string;
  locationName?: string;
  latitude?: number;
  longitude?: number;
  notificationsEnabled?: boolean;
};

export type CreateCountdownInput = {
  title: string;
  category?: string;
  eventAt: number;
  journeyEnabled?: boolean;
  icon?: string;
  note?: string;
};

export type CreateEventInput = {
  title: string;
  category?: string;
  iconKey?: string;
  startAt: number;
  endAt: number;
  allDay?: boolean;
  timeZone?: string;

  location?: string;
  latitude?: number;
  longitude?: number;
  placeId?: string;

  notes?: string;
  repeatRule?: string;
  addToCountdown?: boolean;
  journeyEnabled?: boolean;
};

type RowsResponse<T> = {
  rows: T[];
};

type RowResponse<T> = {
  row: T;
};

function requiredText(
  value: string,
  fieldName: string,
) {
  const result = value.trim();

  if (!result) {
    throw new Error(
      `${fieldName} is required.`,
    );
  }

  return result;
}

function localId() {
  return Date.now();
}

function startOfLocalDay(
  value: Date | number = new Date(),
) {
  const date =
    new Date(value);

  date.setHours(
    0,
    0,
    0,
    0,
  );

  return date.getTime();
}

function withoutUndefined(
  values:
    Record<string, unknown>,
) {
  return Object.fromEntries(
    Object.entries(values).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null,
    ),
  );
}

async function executeBackend<T>(
  action: string,
  values:
    Record<string, unknown> = {},
) {
  if (
    !config.backendFunctionId ||
    config.backendFunctionId ===
      "YOUR_APPWRITE_FUNCTION_ID"
  ) {
    throw new Error(
      "SARADIUM Backend Function ID is missing.",
    );
  }

  const execution =
    await functions.createExecution({
      functionId:
        config.backendFunctionId,

      body: JSON.stringify({
        action,
        ...values,
      }),

      async: false,
      xpath: "/",
      method:
        ExecutionMethod.POST,

      headers: {
        "content-type":
          "application/json",
      },
    });

  let response:
    Record<string, unknown> = {};

  if (
    execution.responseBody
  ) {
    try {
      response =
        JSON.parse(
          execution.responseBody,
        ) as Record<
          string,
          unknown
        >;
    } catch {
      throw new Error(
        "The SARADIUM backend returned an invalid response.",
      );
    }
  }

  const status =
    Number(
      execution
        .responseStatusCode ||
        0,
    );

  if (
    status < 200 ||
    status >= 300
  ) {
    const serverMessage =
      response.message;

    throw new Error(
      typeof serverMessage ===
        "string" &&
      serverMessage
        ? serverMessage
        : "The secure SARADIUM request failed.",
    );
  }

  return response as T;
}

async function listMyRows<
  T extends CloudRow,
>(
  table: string,
  options:
    Record<string, unknown> = {},
) {
  const response =
    await executeBackend<
      RowsResponse<T>
    >(
      "listMyRows",
      {
        table,
        ...options,
      },
    );

  return response.rows ?? [];
}

async function createMyRow<
  T extends CloudRow,
>(
  table: string,
  data:
    Record<string, unknown>,
) {
  const response =
    await executeBackend<
      RowResponse<T>
    >(
      "createMyRow",
      {
        table,
        data:
          withoutUndefined(data),
      },
    );

  return response.row;
}

async function updateMyRow<
  T extends CloudRow,
>(
  table: string,
  rowId: string,
  data:
    Record<string, unknown>,
) {
  const response =
    await executeBackend<
      RowResponse<T>
    >(
      "updateMyRow",
      {
        table,
        rowId,
        data:
          withoutUndefined(data),
      },
    );

  return response.row;
}

async function deleteMyRow(
  table: string,
  rowId: string,
) {
  return executeBackend<{
    success: boolean;
  }>(
    "deleteMyRow",
    {
      table,
      rowId,
    },
  );
}

/* Tasks and reminders */

export async function listTasks() {
  const rows =
    await listMyRows<TaskRow>(
      "tasks",
    );

  return rows.filter(
    (row) => !row.deleted,
  );
}

export async function createTask(
  input: CreateTaskInput,
) {
  const now = Date.now();

  return createMyRow<TaskRow>(
    "tasks",
    {
      localId: localId(),

      title:
        requiredText(
          input.title,
          "Title",
        ),

      note:
        input.note?.trim() ??
        "",

      dueAt:
        Number(input.dueAt),

      repeatRule:
        input.repeatRule?.trim() ??
        "",

      routine:
        input.routine ??
        false,

      completed: false,

      icon:
        input.icon?.trim() ??
        "",

      locationName:
        input.locationName
          ?.trim() ?? "",

      latitude:
        input.latitude,

      longitude:
        input.longitude,

      notificationsEnabled:
        input
          .notificationsEnabled ??
        true,

      deleted: false,
      createdAt: now,
      deviceId: "web",
      syncVersion: 1,
      updatedAt: now,
    },
  );
}

export async function updateTask(
  rowId: string,

  changes:
    Partial<
      CreateTaskInput
    > & {
      completed?: boolean;
    },
) {
  const data:
    Record<string, unknown> = {
      updatedAt: Date.now(),
    };

  if (
    changes.title !== undefined
  ) {
    data.title =
      requiredText(
        changes.title,
        "Title",
      );
  }

  if (
    changes.note !== undefined
  ) {
    data.note =
      changes.note.trim();
  }

  if (
    changes.dueAt !== undefined
  ) {
    data.dueAt =
      Number(
        changes.dueAt,
      );
  }

  if (
    changes.repeatRule !==
    undefined
  ) {
    data.repeatRule =
      changes.repeatRule.trim();
  }

  if (
    changes.routine !== undefined
  ) {
    data.routine =
      changes.routine;
  }

  if (
    changes.completed !==
    undefined
  ) {
    data.completed =
      changes.completed;
  }

  if (
    changes.icon !== undefined
  ) {
    data.icon =
      changes.icon.trim();
  }

  if (
    changes.locationName !==
    undefined
  ) {
    data.locationName =
      changes.locationName
        .trim();
  }

  if (
    changes.latitude !==
    undefined
  ) {
    data.latitude =
      changes.latitude;
  }

  if (
    changes.longitude !==
    undefined
  ) {
    data.longitude =
      changes.longitude;
  }

  if (
    changes
      .notificationsEnabled !==
    undefined
  ) {
    data.notificationsEnabled =
      changes
        .notificationsEnabled;
  }

  return updateMyRow<TaskRow>(
    "tasks",
    rowId,
    data,
  );
}

export async function setTaskCompleted(
  rowId: string,
  completed: boolean,
) {
  return updateTask(
    rowId,
    { completed },
  );
}

export async function deleteTask(
  rowId: string,
) {
  await deleteMyRow(
    "tasks",
    rowId,
  );
}

/* Routine completion */

export async function listRoutineCompletions(
  day: Date | number =
    new Date(),
) {
  return listMyRows<
    RoutineCompletionRow
  >(
    "routine_completions",
    {
      dayStart:
        startOfLocalDay(day),
    },
  );
}

export async function setRoutineCompleted(
  task: TaskRow,
  completed: boolean,

  day: Date | number =
    new Date(),
) {
  const dayStart =
    startOfLocalDay(day);

  const rows =
    await listRoutineCompletions(
      day,
    );

  const existing =
    rows.find(
      (row) =>
        row.taskRowId ===
        task.$id,
    );

  if (existing) {
    return updateMyRow<
      RoutineCompletionRow
    >(
      "routine_completions",
      existing.$id,
      {
        completed,
        updatedAt:
          Date.now(),
      },
    );
  }

  return createMyRow<
    RoutineCompletionRow
  >(
    "routine_completions",
    {
      taskRowId:
        task.$id,

      taskLocalId:
        task.localId,

      dayStart,
      completed,
      updatedAt:
        Date.now(),
    },
  );
}

/* Countdowns */

export async function listCountdowns() {
  const rows =
    await listMyRows<
      CountdownRow
    >(
      "countdowns",
    );

  return rows.filter(
    (row) => !row.deleted,
  );
}

export async function createCountdown(
  input:
    CreateCountdownInput,
) {
  const now = Date.now();

  return createMyRow<
    CountdownRow
  >(
    "countdowns",
    {
      localId: localId(),

      title:
        requiredText(
          input.title,
          "Title",
        ),

      category:
        input.category
          ?.trim() ||
        "Personal",

      eventAt:
        Number(
          input.eventAt,
        ),

      journeyEnabled:
        input.journeyEnabled ??
        false,

      icon:
        input.icon?.trim() ||
        "⌛",

      note:
        input.note?.trim() ??
        "",

      deleted: false,
      createdAt: now,
      deviceId: "web",
      syncVersion: 1,
      updatedAt: now,
    },
  );
}

export async function updateCountdown(
  rowId: string,

  changes:
    Partial<
      CreateCountdownInput
    >,
) {
  const data:
    Record<string, unknown> = {
      updatedAt: Date.now(),
    };

  if (
    changes.title !== undefined
  ) {
    data.title =
      requiredText(
        changes.title,
        "Title",
      );
  }

  if (
    changes.category !==
    undefined
  ) {
    data.category =
      changes.category
        .trim() ||
      "Personal";
  }

  if (
    changes.eventAt !==
    undefined
  ) {
    data.eventAt =
      Number(
        changes.eventAt,
      );
  }

  if (
    changes.journeyEnabled !==
    undefined
  ) {
    data.journeyEnabled =
      changes.journeyEnabled;
  }

  if (
    changes.icon !== undefined
  ) {
    data.icon =
      changes.icon.trim();
  }

  if (
    changes.note !== undefined
  ) {
    data.note =
      changes.note.trim();
  }

  return updateMyRow<
    CountdownRow
  >(
    "countdowns",
    rowId,
    data,
  );
}

export async function deleteCountdown(
  rowId: string,
) {
  await deleteMyRow(
    "countdowns",
    rowId,
  );
}

/* Calendar events */

export async function listEvents() {
  const rows =
    await listMyRows<EventRow>(
      "events",
    );

  return rows.filter(
    (row) => !row.deleted,
  );
}

export async function createEvent(
  input: CreateEventInput,
) {
  const now = Date.now();

  const startAt =
    Number(input.startAt);

  const endAt =
    Math.max(
      Number(input.endAt),
      startAt,
    );

  return createMyRow<EventRow>(
    "events",
    {
      localId: localId(),

      title:
        requiredText(
          input.title,
          "Title",
        ),

      category:
        input.category
          ?.trim() ||
        "Personal",

      iconKey:
        input.iconKey
          ?.trim() ||
        "calendar",

      startAt,
      endAt,

      allDay:
        input.allDay ??
        false,

      timeZone:
        input.timeZone
          ?.trim() ||
        Intl.DateTimeFormat()
          .resolvedOptions()
          .timeZone,

      location:
        input.location
          ?.trim() ?? "",

      latitude:
        input.latitude,

      longitude:
        input.longitude,

      placeId:
        input.placeId
          ?.trim() ?? "",

      notes:
        input.notes
          ?.trim() ?? "",

      imageUri: "",
      cloudImageFileId: "",

      repeatRule:
        input.repeatRule
          ?.trim() ?? "",

      addToCountdown:
        input.addToCountdown ??
        false,

      linkedCountdownId: 0,

      journeyEnabled:
        input.journeyEnabled ??
        false,

      deleted: false,
      createdAt: now,
      updatedAt: now,
    },
  );
}

export async function deleteEvent(
  rowId: string,
) {
  await deleteMyRow(
    "events",
    rowId,
  );
}

/* Preferences */

export async function getPreferences() {
  const rows =
    await listMyRows<
      UserPreferencesRow
    >(
      "user_preferences",
    );

  return rows[0] ?? null;
}

export async function savePreferences(
  changes:
    Partial<
      Pick<
        UserPreferencesRow,
        | "theme"
        | "style"
        | "myState"
        | "timeZone"
        | "notificationsEnabled"
      >
    >,
) {
  const existing =
    await getPreferences();

  const data = {
    theme:
      changes.theme ??
      existing?.theme ??
      "SYSTEM",

    style:
      changes.style ??
      existing?.style ??
      "LIQUID_GLASS",

    myState:
      changes.myState ??
      existing?.myState ??
      "Penang",

    timeZone:
      changes.timeZone ??
      existing?.timeZone ??
      Intl.DateTimeFormat()
        .resolvedOptions()
        .timeZone,

    notificationsEnabled:
      changes
        .notificationsEnabled ??
      existing
        ?.notificationsEnabled ??
      true,

    updatedAt:
      Date.now(),
  };

  if (existing) {
    return updateMyRow<
      UserPreferencesRow
    >(
      "user_preferences",
      existing.$id,
      data,
    );
  }

  return createMyRow<
    UserPreferencesRow
  >(
    "user_preferences",
    data,
  );
}

/* Profile */

export async function getMyProfile() {
  return executeBackend<
    ProfileIdentity
  >(
    "getMyProfile",
  );
}

export async function createMyProfile(
  displayName: string,
  username: string,
) {
  return executeBackend<
    ProfileIdentity
  >(
    "createMyProfile",
    {
      displayName:
        requiredText(
          displayName,
          "Display name",
        ),

      username:
        requiredText(
          username,
          "Username",
        )
          .toLowerCase(),
    },
  );
}

export async function discardUnprofiledAccount() {
  return executeBackend<{
    success: boolean;
  }>(
    "discardUnprofiledAccount",
  );
}

/* Subscription entitlement */

export async function getMyEntitlement() {
  const response =
    await executeBackend<{
      row:
        | EntitlementRow
        | null;
    }>(
      "getMyEntitlement",
    );

  return response.row;
}

/* Feedback */

export async function submitFeedback(
  category: string,
  message: string,
) {
  const cleanMessage =
    requiredText(
      message,
      "Feedback",
    );

  const now = Date.now();

  return createMyRow<CloudRow>(
    "feedback",
    {
      category:
        category.trim() ||
        "GENERAL",

      message:
        cleanMessage,

      status: "NEW",
      createdAt: now,
      updatedAt: now,
    },
  );
}

/* Official live Malaysian calendar */

export async function getMalaysiaHolidays(
  year: number,
) {
  if (
    !Number.isInteger(year) ||
    year < 2023 ||
    year >
      new Date().getFullYear() + 2
  ) {
    throw new Error(
      "Unsupported calendar year.",
    );
  }

  return executeBackend<
    MalaysiaHolidayResponse
  >(
    "getMalaysiaHolidays",
    { year },
  );
}